import { Controller } from '@hotwired/stimulus'

const BADGE_PILL_CLASSES = ['px-8', 'py-4', 'rounded-8'];

export default class extends Controller {
  static targets = [
    'button', 'label', 'caret', 'item', 'check',
    'list', 'search', 'clearSearchBtn', 'empty',
    'hiddenField'
  ];

  static values = {
    direction: { type: String, default: 'auto' },
    align: { type: String, default: 'left' },
    minWidth: Number,
    maxWidth: Number,
    autoclose: { type: Number, default: 5000 },
    mode: { type: String, default: 'menu' }, // 'menu' | 'select'
    selectedValue: String,
    viewOnly: { type: Boolean, default: false },
    disableDeselect: { type: Boolean, default: false },
  }

  connect() {
    const dd = this.element.querySelector('[data-ss-dropdown]')
    if (!dd) return
    this.dropdownEl = dd
    this.dropdownOriginParent = dd.parentElement

    this.portalRoot = this._ensurePortalRoot()
    this._autocloseTimer = null

    // lock width for the duration of one open
    this._widthLocked = false
    this._lockedWidthPx = null

    if (this.isSelectMode) {
      this._syncChecksToSelected();
      this._syncLabelToSelected();
    }
  }

  disconnect() {
    this._teardownOutside()
    this._detachObservers()
    this._unbindDropdownHandlers()
    this._clearAutoclose()
    this._teardownFocusAutoclose();
    if (this.dropdownEl && this.dropdownEl.parentElement === this.portalRoot) {
      this.dropdownOriginParent.appendChild(this.dropdownEl)
    }
  }

  // === Helpers ===
  get isSelectMode() {
    return (this.hasModeValue ? this.modeValue : 'menu') === 'select';
  }

  // API
  toggle(e) {
    e?.preventDefault();
    e?.stopPropagation();
    if (this.viewOnlyValue) return;
    this.isDropdownOpen ? this.close() : this.open();
  }

  open() {
    if (this.isDropdownOpen || !this.dropdownEl) return

    this.portalRoot.appendChild(this.dropdownEl)

    this.dropdownEl.classList.remove('hidden')
    this.dropdownEl.classList.add('invisible','ss-transition-none','opacity-0','scale-95')

    this.buttonTarget.setAttribute('aria-expanded','true')
    if (this.isSelectMode && this.hasCaretTarget) {
      this.caretTarget.classList.add('rotate-180');
    }

    this._widthLocked = false
    this._lockedWidthPx = null

    this._positionDropdown()
    this._bindDropdownHandlers()

    this._setupFocusAutoclose();

    this._maybeArmAutoclose();


    // reset search/filter (if exists)
    this._grabRefs()
    if (this._searchEl) {
      this._searchEl.value = ''
      this._toggleClearBtn(false)
      this._filter('')
      this._positionDropdown()
      try { this._searchEl.focus({ preventScroll: true }) } catch(_) {}
    }

    if (this.isSelectMode) this._syncChecksToSelected()

    void this.dropdownEl.offsetHeight
    this.dropdownEl.classList.remove('ss-transition-none','invisible')
    requestAnimationFrame(() => {
      this.dropdownEl.classList.remove('opacity-0','scale-95')
      this.dropdownEl.classList.add('opacity-100','scale-100')
    })

    this._setupOutside()
    this._attachObservers()

    this.isDropdownOpen = true
  }

  close() {
    if (!this.isDropdownOpen || !this.dropdownEl) return

    this.dropdownEl.classList.add('opacity-0','scale-95')
    this.dropdownEl.classList.remove('opacity-100','scale-100')

    const onEnd = () => {
      this.dropdownEl.classList.add('hidden')
      this.dropdownEl.removeEventListener('transitionend', onEnd)
    }
    this.dropdownEl.addEventListener('transitionend', onEnd)

    this.buttonTarget.setAttribute('aria-expanded','false')
    if (this.isSelectMode && this.hasCaretTarget) {
      this.caretTarget.classList.remove('rotate-180');
    }

    this._unbindDropdownHandlers()
    this._teardownOutside()
    this._detachObservers()
    this._clearAutoclose()
    this._teardownFocusAutoclose();

    this.isDropdownOpen = false
  }

  // Handle <li data-action="click->menu#click">
  click(e) { this._onItemClick(e) }

  // User selection (delegated)
  _onItemClick = (e) => {
    if (this.viewOnlyValue) return;
    const li = e.target.closest('li[role="menuitem"]')
    if (!li || !this.dropdownEl?.contains(li)) return
    if (li.getAttribute('aria-disabled') === 'true') return;

    // Don't close menu when clicking toggle items
    const isToggleItem = li.dataset.menuItemType === 'toggle';
    if (isToggleItem) return;

    if (this.isSelectMode) {
      const value = li.dataset.value ?? li.querySelector('[data-label]')?.textContent?.trim();
      const label = li.querySelector('[data-label]')?.textContent?.trim();
      const index = Number(li.dataset.index ?? -1);

      const isDeselecting = this.selectedValueValue === value;

      if (isDeselecting && this.disableDeselectValue) {
        requestAnimationFrame(() => this.close());
        return;
      }

      if (isDeselecting) {
        this.selectedValueValue = '';

        if (this.hasHiddenFieldTarget) {
          this.hiddenFieldTarget.value = '';
        }

        if (this.hasLabelTarget) {
          const defaultLabel = this.buttonTarget.dataset.defaultLabel || '';
          this.labelTarget.textContent = defaultLabel;
          this._applyLabelItemClass(null);
        }

        this.dispatch('change', {
          detail: { value: '', label: '', index: -1 },
          prefix: this.identifier,
          bubbles: true,
        });

        this._syncChecksToSelected();
      } else {
        this.selectedValueValue = value ?? '';

        if (this.hasHiddenFieldTarget) {
          this.hiddenFieldTarget.value = value ?? '';
        }

        if (this.hasLabelTarget && label) {
          this.labelTarget.textContent = label;
          this._applyLabelItemClass(li.dataset.itemClass);
        }

        this.dispatch('change', {
          detail: { value, label, index },
          prefix: this.identifier,
          bubbles: true,
        });

        this._syncChecksToSelected();
      }
    }
    requestAnimationFrame(() => this.close())
  }

  // === Stimulus Values hooks ===
  selectedValueValueChanged() {
    if (!this.isSelectMode) return;
    this._syncChecksToSelected();
  }

  // Bind after teleporting
  _bindDropdownHandlers() {
    if (this._dropdownHandlersBound || !this.dropdownEl) return

    this.dropdownEl.addEventListener('click', this._onItemClick)

    // Gather references within the dropdown (after teleporting)
    this._grabRefs()

    // Search handlers
    if (this._searchEl) {
      this._onSearchInput = () => {
        const q = this._normalize(this._searchEl.value || '')
        this._toggleClearBtn(q.length > 0)
        this._filter(q)
        this._positionDropdown() // height can change after filtering, so always recalc
      }

      this._onSearchKeydown = (e) => {
        if (!e) return
        if (e.key === 'Escape') {
          if (this._searchEl.value) {
            this._searchEl.value = ''
            this._toggleClearBtn(false)
            this._filter('')
            this._positionDropdown()
          } else {
            this.close()
          }
          e.preventDefault()
        } else if (e.key === 'Enter') {
          const visible = this._visibleItems()
          if (visible.length === 1) {
            visible[0].click()
            e.preventDefault()
          }
        }
      }

      // Stop autoclose on focus in, restart on focus out
      this._onFocusIn = (e) => {
        if (e.target === this._searchEl || this._searchEl.contains(e.target)) this._clearAutoclose()
      }
      this._onFocusOut = (e) => {
        if (this.isDropdownOpen) this._startAutoclose()
      }

      this._searchEl.addEventListener('input', this._onSearchInput)
      this._searchEl.addEventListener('keydown', this._onSearchKeydown)
      this.dropdownEl.addEventListener('focusin', this._onFocusIn)
      this.dropdownEl.addEventListener('focusout', this._onFocusOut)
    }

    if (this._clearBtnEl) {
      this._onClearClick = (e) => {
        e?.preventDefault(); e?.stopPropagation()
        if (!this._searchEl) return
        this._searchEl.value = ''
        this._toggleClearBtn(false)
        this._filter('')
        try { this._searchEl.focus({ preventScroll: true }) } catch(_) {}
        this._positionDropdown()
      }
      this._clearBtnEl.addEventListener('click', this._onClearClick)
    }

    this._dropdownHandlersBound = true
  }

  _unbindDropdownHandlers() {
    if (!this._dropdownHandlersBound || !this.dropdownEl) return

    this.dropdownEl.removeEventListener('click', this._onItemClick)

    if (this._searchEl) {
      this._searchEl.removeEventListener('input', this._onSearchInput)
      this._searchEl.removeEventListener('keydown', this._onSearchKeydown)
      this.dropdownEl.removeEventListener('focusin', this._onFocusIn)
      this.dropdownEl.removeEventListener('focusout', this._onFocusOut)
    }
    if (this._clearBtnEl) {
      this._clearBtnEl.removeEventListener('click', this._onClearClick)
    }

    this._onSearchInput = null
    this._onSearchKeydown = null
    this._onClearClick = null
    this._onFocusIn = null
    this._onFocusOut = null

    this._dropdownHandlersBound = false
  }

  _grabRefs() {
    if (!this.dropdownEl) return
    this._searchEl = this.dropdownEl.querySelector('[data-menu-target="search"]') || null
    this._listEl = this.dropdownEl.querySelector('[data-menu-target="list"]') || null
    this._clearBtnEl = this.dropdownEl.querySelector('[data-menu-target="clearSearchBtn"]') || null
    this._emptyEl = this.dropdownEl.querySelector('[data-menu-target="empty"]') || null
  }

  // Portal
  _ensurePortalRoot() {
    let root = document.getElementById('ui-layer-root')
    if (!root) {
      root = document.createElement('div')
      root.id = 'ui-layer-root'
      root.classList.add('ui-layer-root')
      document.body.appendChild(root)
    }
    return root
  }

  // Positioning
  _positionDropdown() {
    const btnRect = this.buttonTarget.getBoundingClientRect()
    const vw = window.innerWidth
    const vh = window.innerHeight
    const M = 8

    const minW = this.hasMinWidthValue ? this.minWidthValue : 200
    const maxW = this.hasMaxWidthValue ? this.maxWidthValue : Infinity

    // WIDTH: locking width on first open
    if (!this._widthLocked) {
      this.dropdownEl.classList.add('ss-no-max-h')
      const naturalW = Math.max(this.dropdownEl.offsetWidth, 0)
      this.dropdownEl.classList.remove('ss-no-max-h')
      const width = Math.max(minW, Math.min(Math.max(btnRect.width, naturalW), maxW))
      this._lockedWidthPx = Math.round(width)
      this._widthLocked = true
    }
    const width = this._lockedWidthPx ?? Math.max(btnRect.width, minW)
    this._setVar('--ss-width', `${Math.round(width)}px`)

    // HEIGHT: can change after filtering, so always recalc
    this.dropdownEl.classList.add('ss-no-max-h')
    const naturalH = this.dropdownEl.offsetHeight || 300
    this.dropdownEl.classList.remove('ss-no-max-h')

    const spaceBelow = vh - btnRect.bottom - M
    const spaceAbove = btnRect.top - M
    const wantsTop = this.directionValue === 'top'
    const wantsBottom = this.directionValue === 'bottom'
    const openUp = wantsTop ? true : wantsBottom ? false : spaceBelow < Math.min(naturalH, 320) && spaceAbove > spaceBelow

    let top = openUp ? Math.max(M, btnRect.top - Math.min(naturalH, spaceAbove)) : btnRect.bottom + 4
    let maxH = openUp ? spaceAbove : spaceBelow
    maxH = Math.max(120, Math.min(naturalH, maxH))

    let left = this.alignValue === 'right' ? btnRect.right - width : btnRect.left
    if (left + width + M > vw) left = Math.max(M, vw - width - M)
    if (left < M) left = M

    this._setVar('--ss-left', `${Math.round(left)}px`)
    this._setVar('--ss-top', `${Math.round(top)}px`)
    this._setVar('--ss-max-h', `${Math.floor(maxH)}px`)
  }

  _setVar(name, value) { this.dropdownEl.style.setProperty(name, value) }

  // Outside/Esc
  _setupOutside() {
    if (this.outsideBound) return

    this._onDocPointerDown = (e) => {
      const t = e?.target
      const insideDropdown = t && this.dropdownEl.contains(t)
      const insideComponent = t && this.element.contains(t)
      if (!insideDropdown && !insideComponent) this.close()
    }
    this._onDocKeydown = (e) => { if (e?.key === 'Escape') this.close() }

    document.addEventListener('pointerdown', this._onDocPointerDown, true)
    document.addEventListener('keydown', this._onDocKeydown)
    this.outsideBound = true
  }

  _teardownOutside() {
    if (!this.outsideBound) return
    document.removeEventListener('pointerdown', this._onDocPointerDown, true)
    document.removeEventListener('keydown', this._onDocKeydown)
    this.outsideBound = false
  }

  // Relayout
  _attachObservers() {
    this._reposition = (e) => {
      if (!this.isDropdownOpen) return
      const t = e?.target
      if (t instanceof Node && (this.dropdownEl?.contains(t) || this.element?.contains(t))) return
      this._positionDropdown()
    }
    window.addEventListener('scroll', this._reposition, true)
    window.addEventListener('resize', this._reposition)

   this._onAnyScrollClose = (e) => {
     if (!this.isDropdownOpen) return;
     const t = e?.target;
     if (t && (this.dropdownEl?.contains(t) || this.element?.contains(t))) return;
     this.close();
   };
   document.addEventListener('scroll', this._onAnyScrollClose, true);
   window.addEventListener('scroll', this._onAnyScrollClose, { passive: true });
   document.addEventListener('wheel', this._onAnyScrollClose, { passive: true, capture: true });
   document.addEventListener('touchmove', this._onAnyScrollClose, { passive: true, capture: true });



    this.ro = new ResizeObserver(this._reposition)
    this.ro.observe(this.buttonTarget)

    if (window.visualViewport) {
      this.vvo = { onResize: this._reposition, onScroll: this._reposition }
      window.visualViewport.addEventListener('resize', this.vvo.onResize)
      window.visualViewport.addEventListener('scroll', this.vvo.onScroll)

      this._onVVScrollClose = () => { if (this.isDropdownOpen) this.close(); };
      window.visualViewport.addEventListener('scroll', this._onVVScrollClose);
    }
  }

  _detachObservers() {
    window.removeEventListener('scroll', this._reposition, true)
    window.removeEventListener('resize', this._reposition)
   document.removeEventListener('scroll', this._onAnyScrollClose, true)
   window.removeEventListener('scroll', this._onAnyScrollClose)
   document.removeEventListener('wheel', this._onAnyScrollClose, true)
   document.removeEventListener('touchmove', this._onAnyScrollClose, true)
   this._onAnyScrollClose = null

    this.ro?.disconnect(); this.ro = undefined
    if (this.vvo) {
      window.visualViewport.removeEventListener('resize', this.vvo.onResize)
      window.visualViewport.removeEventListener('scroll', this.vvo.onScroll)
      window.visualViewport.removeEventListener('scroll', this._onVVScrollClose)
      this._onVVScrollClose = null
      this.vvo = undefined
    }
  }

  // Autoclose
  _startAutoclose() {
    this._clearAutoclose()
    const ms = this.hasAutocloseValue ? this.autocloseValue : 5000
    if (!ms || ms <= 0) return
    this._autocloseTimer = setTimeout(() => this.close(), ms)
  }
  _clearAutoclose() { if (this._autocloseTimer) { clearTimeout(this._autocloseTimer); this._autocloseTimer = null } }

  // === Select helpers ===
  _syncChecksToSelected() {
    if (!this.dropdownEl) return;
    const selected = (this.hasSelectedValueValue ? this.selectedValueValue : '').toString();
    const items = this.dropdownEl.querySelectorAll('[data-menu-target="item"]');
    items.forEach((li) => {
      const isSelected = selected && (li.dataset.value || '').toString() === selected;
      li.setAttribute('aria-checked', isSelected ? 'true' : 'false');
      const checkEl = li.querySelector('[data-menu-target="check"]');
      if (checkEl) checkEl.classList.toggle('hidden', !isSelected);
    });
  }

  _syncLabelToSelected() {
    if (!this.dropdownEl || !this.hasLabelTarget) return;
    const selected = (this.hasSelectedValueValue ? this.selectedValueValue : '').toString();
    if (!selected) return;

    const items = this.dropdownEl.querySelectorAll('[data-menu-target="item"]');
    items.forEach((li) => {
      const isSelected = selected && (li.dataset.value || '').toString() === selected;
      if (isSelected) {
        const label = li.querySelector('[data-label]')?.textContent?.trim();
        const itemClass = li.dataset.itemClass;

        if (label) {
          this.labelTarget.textContent = label;
          this._applyLabelItemClass(itemClass);
        }
      }
    });
  }

  _applyLabelItemClass(itemClass) {
    const storedClass = this.labelTarget.dataset.badgeClass;
    if (storedClass) {
      const storedTokens = storedClass.split(/\s+/).filter(Boolean);
      if (storedTokens.length) this.labelTarget.classList.remove(...storedTokens);
    }

    const itemTokens = (itemClass || '').split(/\s+/).filter(Boolean);
    if (!itemTokens.length) {
      delete this.labelTarget.dataset.badgeClass;
      return;
    }

    const appliedTokens = itemTokens.some((token) => token.startsWith('badge-'))
      ? [...itemTokens, ...BADGE_PILL_CLASSES]
      : itemTokens;
    this.labelTarget.classList.add(...appliedTokens);
    this.labelTarget.dataset.badgeClass = appliedTokens.join(' ');
  }

  // === Search helpers ===
  _normalize(str) {
    return (str || '').toString().toLowerCase().trim()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
  }

  _toggleClearBtn(show) {
    if (this._clearBtnEl) this._clearBtnEl.classList.toggle('hidden', !show)
  }

  _filter(q) {
    const list = this._listEl || this.dropdownEl
    const items = list.querySelectorAll('li[role="menuitem"][data-value]')
    const nn = this._normalize(q)
    let visible = 0

    items.forEach((li) => {
      const raw = li.getAttribute('data-label') || li.querySelector('[data-label]')?.textContent || ''
      const text = this._normalize(raw)
      const match = !nn || text.includes(nn)
      li.hidden = !match
      if (match) visible += 1
    })

    if (this._emptyEl) this._emptyEl.hidden = !(nn && visible === 0)
  }

  _visibleItems() {
    const list = this._listEl || this.dropdownEl
    return Array.from(list.querySelectorAll('li[role="menuitem"][data-value]')).filter(li => !li.hidden)
  }

  _hasFocusInside() {
  const ae = document.activeElement;
  return !!(ae && (this.dropdownEl?.contains(ae) || this.element.contains(ae)));
}

_maybeArmAutoclose() {
  if (!this._hasFocusInside()) this._startAutoclose();
}

_setupFocusAutoclose() {
  if (this._focusGuardsBound || !this.dropdownEl) return;

  this._onFocusIn = () => this._clearAutoclose();

  this._onFocusOut = () => {
    setTimeout(() => this._maybeArmAutoclose(), 0);
  };

  this.dropdownEl.addEventListener('focusin', this._onFocusIn);
  this.dropdownEl.addEventListener('focusout', this._onFocusOut);
  this.buttonTarget.addEventListener('focusin', this._onFocusIn);
  this.buttonTarget.addEventListener('focusout', this._onFocusOut);

  this._focusGuardsBound = true;
}

_teardownFocusAutoclose() {
  if (!this._focusGuardsBound) return;
  this.dropdownEl?.removeEventListener('focusin', this._onFocusIn);
  this.dropdownEl?.removeEventListener('focusout', this._onFocusOut);
  this.buttonTarget?.removeEventListener('focusin', this._onFocusIn);
  this.buttonTarget?.removeEventListener('focusout', this._onFocusOut);
  this._onFocusIn = null;
  this._onFocusOut = null;
  this._focusGuardsBound = false;
}

}
